import { Field } from 'formik';
import React from 'react';
import { envConfig } from '../../../../utils/config';

type Props = {
  disabled: boolean;
  name: string;
  size?: string;
  type?: string;
  rows?: number;
  placeholder?: string;
  isNumber?: boolean;
  onClick?: () => void;
  maxLength?: number;
  maxLengthMessage?: string;
};

const CustomFormikInput: React.FC<Props> = ({
  name,
  disabled,
  size = envConfig.DEFAULT_INPUT_FIELD_SIZE,
  type,
  rows,
  placeholder,
  isNumber = false,
  onClick,
  maxLength,
  maxLengthMessage,
}) => {
  return (
    <Field name={name}>
      {({ field, form }: any) => {
        const safeValue = field.value ?? '';

        //  only allow numbers in state
        const handleChange = (e: React.ChangeEvent<any>) => {
          let value = e.target.value;

          //  If isNumber is true, only allow digits
          if (isNumber) {
            value = value.replace(/[^0-9.]/g, '');
          }

          if (maxLength && value.length > maxLength) {
            form.setFieldValue(name, value.slice(0, maxLength), false);
            form.setFieldTouched(name, true, false);
            form.setFieldError(
              name,
              maxLengthMessage ?? `Must be ${maxLength} characters or less`
            );
            return;
          }

          form.setFieldValue(name, value);
        };

        //  BLOCK invalid keys like e, E, +, -
        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (isNumber) {
            if (['e', 'E', '+', '-'].includes(e.key)) {
              e.preventDefault();
            }
          }
        };

        //  BLOCK paste of non-numeric values
        const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
          if (isNumber) {
            const text = e.clipboardData.getData("text/plain");
            if (!/^\d+$/.test(text)) {
              e.preventDefault();
            }
          }
        };

        //  Clear 0 on focus for number inputs
        const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          if (Number(field.value) === 0) {
            form.setFieldValue(name, '');
          }
        };

        const handleBlur = () => {
          if (isNumber && field.value === '') {
            form.setFieldValue(name, 0);
          }
        }

        const commonProps = {
          ...field,
          onChange: handleChange,
          onKeyDown: handleKeyDown,
          onPaste: handlePaste,
          onFocus: handleFocus,
          onBlur: handleBlur,
          className: `form-control form-control-${size || 'lg'} form-control-solid`,
          placeholder,
          disabled,
          name,
          onClick,
        };

        return type === 'textarea' ? (
          <textarea {...commonProps} rows={rows} value={safeValue} />
        ) : (
          <input
            {...commonProps}
            type={type || 'text'}
            value={safeValue}
            inputMode={isNumber ? 'numeric' : undefined}
          />
        );
      }}
    </Field>
  );
};

export default CustomFormikInput;